import {
  InvalidConfigError,
  MessageFormatError,
  MissingDataError,
  MissingTranslationError,
  UnsupportedFormatterError
} from '@formatjs/intl'
import {FormatError} from 'intl-messageformat'

import {logger} from '../../utils/logging/logger'

type IntlErrors =
  | MissingTranslationError
  | MessageFormatError
  | MissingDataError
  | InvalidConfigError
  | UnsupportedFormatterError
  | FormatError

export interface LogPayload {
  errorName: string
  errorMessage: string
  translationId?: string
  locale?: string
}

/**
 * Checks if an error is a missing translation or message format error.
 * @param error - The error to check
 * @returns True if the error is a MissingTranslationError or MessageFormatError
 */
export const getIsMissingTranslationOrMessageFormatError = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any
): error is MissingTranslationError | MessageFormatError =>
  Boolean(error?.descriptor?.id && error?.descriptor?.defaultMessage)

/**
 * Creates a translation error logger function.
 * @param locale - The current locale
 * @param translationErrorLogger - Function to log error data
 * @returns A function that handles translation errors
 */
export const getTranslationErrorLogger =
  (locale: string, translationErrorLogger: (logData: LogPayload) => void) =>
  (error: IntlErrors) => {
    if (
      process.env.NODE_ENV === 'production' &&
      getIsMissingTranslationOrMessageFormatError(error)
    ) {
      const payload: LogPayload = {
        errorName: error.name,
        errorMessage: error.message,
        translationId: error.descriptor?.id,
        locale
      }

      try {
        translationErrorLogger(payload)
      } catch (error) {
        logger.error('Error getting translation logger:', error)
      }
    } else if (process.env.NODE_ENV !== 'production') {
      logger.error('Error getting translation logger:', error)
    }
  }
